import type { Fonction } from '../@types/Fonction';
import type { User } from '../@types/User';

export type FonctionResultLike = {
  id?: number;
  type?: number;
  company?: Fonction['company'];
  user?: User | null;
};

const normalizeFonctions = (fonctions: Fonction[] | undefined): Fonction[] => (
  Array.isArray(fonctions) ? fonctions : []
);

const buildFonctionFromResult = (fonctionResult: FonctionResultLike): Fonction | null => {
  if (
    typeof fonctionResult.id !== 'number'
    || typeof fonctionResult.type !== 'number'
    || !fonctionResult.company
  ) {
    return null;
  }

  return {
    id: fonctionResult.id,
    type: fonctionResult.type,
    company: fonctionResult.company,
  };
};

const appendFonctionIfMissing = (fonctions: Fonction[], fonction: Fonction | null): Fonction[] => {
  if (!fonction) {
    return fonctions;
  }

  if (fonctions.some((candidate) => candidate.id === fonction.id)) {
    return fonctions;
  }

  return [...fonctions, fonction];
};

export const getUserFonctionTypesForCompany = (
  user: User | null | undefined,
  companyId: number | null | undefined,
): number[] => {
  if (!user || companyId === null || companyId === undefined) {
    return [];
  }

  return normalizeFonctions(user.fonctions)
    .filter((fonction) => fonction.company?.id === companyId)
    .map((fonction) => fonction.type);
};

export const extractUniqueUsersFromFonctionResults = (
  fonctionResults: FonctionResultLike[] = [],
  predicate?: (fonctionResult: FonctionResultLike) => boolean,
): User[] => {
  const usersById = new Map<number, User>();
  const safeResults = Array.isArray(fonctionResults) ? fonctionResults : [];

  safeResults.forEach((fonctionResult) => {
    if (predicate && !predicate(fonctionResult)) {
      return;
    }

    const user = fonctionResult.user;
    if (!user || typeof user.id !== 'number') {
      return;
    }

    const fonction = buildFonctionFromResult(fonctionResult);
    const existingUser = usersById.get(user.id);

    if (!existingUser) {
      usersById.set(user.id, {
        ...user,
        fonctions: appendFonctionIfMissing(normalizeFonctions(user.fonctions), fonction),
      });
      return;
    }

    usersById.set(user.id, {
      ...existingUser,
      fonctions: appendFonctionIfMissing(normalizeFonctions(existingUser.fonctions), fonction),
    });
  });

  return [...usersById.values()];
};
